import { Injectable, InjectionToken } from '@angular/core'

import { Store } from '@ngrx/store'
import { Observable } from 'rxjs'

import {
  EQuarterlyDashboardViewMode,
  IQuarterlyDashboardMktCap,
  IQuarterlyDashboardQuarter,
  IQuarterlyDashboardSector,
  IQuarterlyDashboardSectorTop500,
} from '~/core/models'

import * as QUARTERLY_DASHBOARD_ACTIONS from './quarterly-dashboard.actions'
import {
  selectQuarterlyDashboardMktCap,
  selectQuarterlyDashboardMktCapData,
  selectQuarterlyDashboardMktCapError,
  selectQuarterlyDashboardMktCapInRequest,
  selectQuarterlyDashboardMktCapOrder,
  selectQuarterlyDashboardMktCapSortBy,
  selectQuarterlyDashboardQuarter,
  selectQuarterlyDashboardQuarterData,
  selectQuarterlyDashboardQuarterEffective,
  selectQuarterlyDashboardQuarterError,
  selectQuarterlyDashboardQuarterInRequest,
  selectQuarterlyDashboardQuarterSelected,
  selectQuarterlyDashboardSector,
  selectQuarterlyDashboardSectorData,
  selectQuarterlyDashboardSectorError,
  selectQuarterlyDashboardSectorInRequest,
  selectQuarterlyDashboardSectorOrder,
  selectQuarterlyDashboardSectorSortBy,
  selectQuarterlyDashboardSectorTop500,
  selectQuarterlyDashboardSectorTop500Data,
  selectQuarterlyDashboardSectorTop500Error,
  selectQuarterlyDashboardSectorTop500InRequest,
  selectQuarterlyDashboardSectorTop500Order,
  selectQuarterlyDashboardSectorTop500SortBy,
  selectQuarterlyDashboardViewMode,
} from './quarterly-dashboard.selectors'
import {
  IQuarterlyDashboardMktCapState,
  IQuarterlyDashboardQuarterState,
  IQuarterlyDashboardSectorState,
  IQuarterlyDashboardSectorTop500State,
} from './quarterly-dashboard.state'
import { IAppWithQuarterlyDashboardState } from './quarterly-dashboard.state'

export interface IQuarterlyDashboardFacade {
  // quarter
  quarter$: Observable<IQuarterlyDashboardQuarterState>
  quarterData$: Observable<IQuarterlyDashboardQuarter[]>
  quarterInRequest$: Observable<boolean>
  quarterError$: Observable<Error | undefined>
  quarterSelected$: Observable<IQuarterlyDashboardQuarter | undefined>
  quarterEffective$: Observable<IQuarterlyDashboardQuarter | undefined>
  // market cap
  mktCap$: Observable<IQuarterlyDashboardMktCapState>
  mktCapData$: Observable<IQuarterlyDashboardMktCap[]>
  mktCapInRequest$: Observable<boolean>
  mktCapError$: Observable<Error | undefined>
  mktCapSortBy$: Observable<string>
  mktCapOrder$: Observable<'asc' | 'desc' | ''>
  // sector
  sector$: Observable<IQuarterlyDashboardSectorState>
  sectorData$: Observable<IQuarterlyDashboardSector[]>
  sectorInRequest$: Observable<boolean>
  sectorError$: Observable<Error | undefined>
  sectorSortBy$: Observable<string>
  sectorOrder$: Observable<'asc' | 'desc' | ''>
  // sector top 500
  sectorTop500$: Observable<IQuarterlyDashboardSectorTop500State>
  sectorTop500Data$: Observable<IQuarterlyDashboardSectorTop500[]>
  sectorTop500InRequest$: Observable<boolean>
  sectorTop500Error$: Observable<Error | undefined>
  sectorTop500SortBy$: Observable<string>
  sectorTop500Order$: Observable<'asc' | 'desc' | ''>
  // view mode
  viewMode$: Observable<EQuarterlyDashboardViewMode>

  // quarter
  getQuarterlyDashboardQuarterData(): void
  changeQuarterlyDashboardQuarterSelected(quarter: IQuarterlyDashboardQuarter): void
  // market cap
  changeQuarterlyDashboardMktCapSort(sortBy: string, order: 'asc' | 'desc' | ''): void
  // sector
  changeQuarterlyDashboardSectorSort(sortBy: string, order: 'asc' | 'desc' | ''): void
  // sector top 500
  changeQuarterlyDashboardSectorTop500Sort(sortBy: string, order: 'asc' | 'desc' | ''): void
  // view mode
  changeQuarterlyDashboardViewMode(viewMode: EQuarterlyDashboardViewMode): void
}

export const QUARTERLY_DASHBOARD_FACADE = new InjectionToken<IQuarterlyDashboardFacade>('QUARTERLY_DASHBOARD_FACADE')

@Injectable()
export class QuarterlyDashboardFacade implements IQuarterlyDashboardFacade {
  // quarter
  quarter$ = this.store$.select(selectQuarterlyDashboardQuarter)
  quarterData$ = this.store$.select(selectQuarterlyDashboardQuarterData)
  quarterInRequest$ = this.store$.select(selectQuarterlyDashboardQuarterInRequest)
  quarterError$ = this.store$.select(selectQuarterlyDashboardQuarterError)
  quarterSelected$ = this.store$.select(selectQuarterlyDashboardQuarterSelected)
  quarterEffective$ = this.store$.select(selectQuarterlyDashboardQuarterEffective)
  // market cap
  mktCap$ = this.store$.select(selectQuarterlyDashboardMktCap)
  mktCapData$ = this.store$.select(selectQuarterlyDashboardMktCapData)
  mktCapInRequest$ = this.store$.select(selectQuarterlyDashboardMktCapInRequest)
  mktCapError$ = this.store$.select(selectQuarterlyDashboardMktCapError)
  mktCapSortBy$ = this.store$.select(selectQuarterlyDashboardMktCapSortBy)
  mktCapOrder$ = this.store$.select(selectQuarterlyDashboardMktCapOrder)
  // sector
  sector$ = this.store$.select(selectQuarterlyDashboardSector)
  sectorData$ = this.store$.select(selectQuarterlyDashboardSectorData)
  sectorInRequest$ = this.store$.select(selectQuarterlyDashboardSectorInRequest)
  sectorError$ = this.store$.select(selectQuarterlyDashboardSectorError)
  sectorSortBy$ = this.store$.select(selectQuarterlyDashboardSectorSortBy)
  sectorOrder$ = this.store$.select(selectQuarterlyDashboardSectorOrder)
  // sector top 500
  sectorTop500$ = this.store$.select(selectQuarterlyDashboardSectorTop500)
  sectorTop500Data$ = this.store$.select(selectQuarterlyDashboardSectorTop500Data)
  sectorTop500InRequest$ = this.store$.select(selectQuarterlyDashboardSectorTop500InRequest)
  sectorTop500Error$ = this.store$.select(selectQuarterlyDashboardSectorTop500Error)
  sectorTop500SortBy$ = this.store$.select(selectQuarterlyDashboardSectorTop500SortBy)
  sectorTop500Order$ = this.store$.select(selectQuarterlyDashboardSectorTop500Order)
  // view mode
  viewMode$ = this.store$.select(selectQuarterlyDashboardViewMode)

  constructor(private store$: Store<IAppWithQuarterlyDashboardState>) {}

  // quarter
  getQuarterlyDashboardQuarterData(): void {
    this.store$.dispatch(QUARTERLY_DASHBOARD_ACTIONS.getQuarterlyDashboardQuarterData())
  }

  changeQuarterlyDashboardQuarterSelected(quarter: IQuarterlyDashboardQuarter): void {
    this.store$.dispatch(QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardQuarterSelected({ selected: quarter }))
  }

  // market cap
  changeQuarterlyDashboardMktCapSort(sortBy: string, order: 'asc' | 'desc' | ''): void {
    this.store$.dispatch(QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardMktCapSort({ sortBy, order }))
  }

  // sector
  changeQuarterlyDashboardSectorSort(sortBy: string, order: 'asc' | 'desc' | ''): void {
    this.store$.dispatch(QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardSectorSort({ sortBy, order }))
  }

  // sector top 500
  changeQuarterlyDashboardSectorTop500Sort(sortBy: string, order: 'asc' | 'desc' | ''): void {
    this.store$.dispatch(QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardSectorTop500Sort({ sortBy, order }))
  }

  // view mode
  changeQuarterlyDashboardViewMode(viewMode: EQuarterlyDashboardViewMode): void {
    this.store$.dispatch(QUARTERLY_DASHBOARD_ACTIONS.setQuarterlyDashboardViewMode({ viewMode }))
  }
}
